import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { ErrorBoundary, ViewLarge, Viewport, ViewSmall } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { CurrentHotelStore } from 'stores/current-hotel-store';

import { HotelMapModel, PropertyLocationScoreInfoModel, WhatsAroundModuleViewModel } from 'models';

import { UitkCard, UitkCardContent } from 'uitk-react-cards';

import { LazyImage } from 'components/common/lazy/lazy-image';
import { LocationEditorial } from './internals/location-editorial';
import { LocationScore } from 'components/infosite/whats-around/internals/location-score';
import { NavigationRefs } from 'components/infosite/navigation/navigation-refs.tsx';
import { WhatsAroundContent } from './internals/whats-around-content';
import { WhatsAroundHeader } from './internals/whats-around-header';
import { WhatsAroundMap } from './whats-around-map';

export interface WhatsAroundCardProps extends LocalizedComponentProps {
  currentHotel?: CurrentHotelStore;
  mapModel?: HotelMapModel;
}

@withLocalization
@inject('currentHotel')
@observer
export class WhatsAroundCard extends React.Component<WhatsAroundCardProps> {
  public render() {
    const { currentHotel, mapModel } = this.props;
    const whatsAroundModel = currentHotel.hotel && currentHotel.hotel.locationInfo && currentHotel.hotel.locationInfo.whatsAroundModule;
    const locationScore = currentHotel && currentHotel.propertyLocationScoreInfo;
    const walkScoreModel = (currentHotel.hotel && currentHotel.hotel.locationInfo && currentHotel.hotel.locationInfo.walkScore) || {};

    if (!whatsAroundModel) {
      return null;
    }

    return (
      <ErrorBoundary label="hotel.information.whatsAround_1">
        <div className="all-y-margin-three" data-stid="whats-around" ref={NavigationRefs.Location}>
          <UitkCard>
            <UitkCardContent className="uitk-grid uitk-cell">
              <Viewport>
                <ViewSmall>
                  <>
                    <WhatsAroundHeader title={whatsAroundModel.title} className="all-t-padding-six all-l-padding-three" />
                    <Map
                      contentClassName="uitk-cell all-cell-1-1"
                      mapClassName="all-cell-1-1"
                      mapModel={mapModel}
                      whatsAroundModel={whatsAroundModel}
                      locationScore={locationScore}
                    />
                    <WhatsAroundContent className="uitk-cell all-cell-1-1" model={whatsAroundModel} walkScoreModel={walkScoreModel} />
                  </>
                </ViewSmall>
                <ViewLarge>
                  <>
                    <WhatsAroundHeader title={whatsAroundModel.title} className="all-t-padding-six all-l-padding-six" />
                    <Map
                      contentClassName="uitk-cell all-cell-1-2 all-x-padding-three all-y-padding-three"
                      mapModel={mapModel}
                      whatsAroundModel={whatsAroundModel}
                      locationScore={locationScore}
                    />
                    <WhatsAroundContent
                      className="uitk-cell all-cell-1-2 all-x-padding-three all-y-padding-three"
                      model={whatsAroundModel}
                      walkScoreModel={walkScoreModel}
                    />
                  </>
                </ViewLarge>
              </Viewport>
            </UitkCardContent>
          </UitkCard>
        </div>
      </ErrorBoundary>
    );
  }
}

interface MapProps extends WhatsAroundCardProps {
  contentClassName: string;
  mapClassName?: string;
  whatsAroundModel: WhatsAroundModuleViewModel;
  locationScore: PropertyLocationScoreInfoModel;
}

const Map: React.FunctionComponent<MapProps> = ({ contentClassName, mapClassName, mapModel, whatsAroundModel, locationScore }) => {
  const locationScoreView = locationScore ? (
    <LocationScore score={locationScore.score} tags={locationScore.tags} data-stid="location-score" />
  ) : (
    <p className="uitk-card-separator-bottom" />
  );

  return (
    <div className={contentClassName}>
      <LocationEditorial
        destinationPageLink={whatsAroundModel.destinationPageLink}
        body={whatsAroundModel.locationEditorial}
        neighborhoodTitle={whatsAroundModel.neighborhood}
      />
      {locationScoreView}
      {mapModel &&
        whatsAroundModel.mapImageUrl && (
          <LazyImage experimentName="PWA_LAZY_LOAD_IMAGES">
            <div data-stid="whats-around-map" className={`${mapClassName} all-x-margin-three all-t-margin-six all-b-margin-three`}>
              <WhatsAroundMap src={whatsAroundModel.mapImageUrl} mapModel={mapModel} />
            </div>
          </LazyImage>
        )}
    </div>
  );
};
